import { memo, useRef, useEffect } from 'react';
import styles from './mailbox.module.css';

export interface MailItem {
  id: string;
  subject: string;
  isCurrent?: boolean;
}

export interface MailboxProps {
  from: string;
  to?: string;
  subject: string;
  content: string;
  metadata?: Record<string, string>;
  onReply: () => void;
  onArchive: () => void;
  onReport: () => void;
  onLinkClick?: () => void;
  emails?: MailItem[];
  /** When true, hide action buttons (e.g. during feedback overlay) */
  readOnly?: boolean;
  /** When true, apply highlight style to show "what's wrong" is in the email */
  highlight?: boolean;
  /** Strings to highlight inside from, subject, and content (e.g. ["outlook.fr"] for domain) */
  highlightText?: string[];
}

/**
 * Mailbox Component
 * 
 * Displays an email with sender, subject, content and action buttons.
 * Designed to be positioned as a scene element.
 */
export const Mailbox = memo(({
  from,
  to,
  subject,
  content,
  metadata = {},
  onReply,
  onArchive,
  onReport,
  onLinkClick,
  emails = [],
  readOnly = false,
  highlight = false,
  highlightText = []
}: MailboxProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const escapeHtml = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const applyHighlights = (text: string): string => {
    if (!highlightText.length) return escapeHtml(text);
    let out = escapeHtml(text);
    highlightText.forEach((term) => {
      if (!term) return;
      const re = new RegExp(escapeRegex(term), 'gi');
      out = out.replace(re, (match) => `<mark class="${styles.emailHighlight}">${escapeHtml(match)}</mark>`);
    });
    return out;
  };
  const applyHighlightsInHtml = (html: string): string => {
    if (!highlightText.length) return html;
    let out = html;
    highlightText.forEach((term) => {
      if (!term) return;
      const re = new RegExp(escapeRegex(term), 'gi');
      out = out.replace(re, (match) => `<mark class="${styles.emailHighlight}">${escapeHtml(match)}</mark>`);
    });
    return out;
  };

  // Add tooltips to all links after render and handle clicks
  useEffect(() => {
    if (!contentRef.current) return;

    const links = contentRef.current.querySelectorAll('a');
    const handlers: Array<{ element: HTMLAnchorElement; handler: (e: Event) => void }> = [];
    
    links.forEach(link => {
      const href = link.getAttribute('href');
      if (href) {
        // Add title attribute to show URL on hover
        link.setAttribute('title', `Lien vers: ${href}`);
        
        // Handle click on link
        const clickHandler = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          
          // Call onLinkClick if available
          if (onLinkClick) {
            onLinkClick();
          }
        };
        
        link.addEventListener('click', clickHandler);
        handlers.push({ element: link as HTMLAnchorElement, handler: clickHandler });
        
        // Make link look active only if onLinkClick is provided
        if (onLinkClick) {
          link.style.cursor = 'pointer';
        } else {
          link.style.cursor = 'default';
          link.style.color = '#999';
          link.style.textDecoration = 'none';
        }
      }
    });
    
    // Cleanup event listeners
    return () => {
      handlers.forEach(({ element, handler }) => {
        element.removeEventListener('click', handler);
      });
    };
  }, [content, onLinkClick]);

  return (
    <div className={`${styles.mailbox} ${highlight ? styles.mailboxHighlight : ''}`}>
      {emails.length > 0 && (
        <div className={styles.sidebar}>
          <div className={styles.sidebarHeader}>Mails</div>
          <div className={styles.mailList}>
            {emails.map((mail) => (
              <div 
                key={mail.id} 
                className={`${styles.mailListItem} ${mail.isCurrent ? styles.activeMail : styles.disabledMail}`}
              >
                {mail.subject}
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className={styles.mainContent}>
        <div className={styles.header}>
          <div className={styles.metadataRow}>
            <span className={styles.label}>De:</span>
            {highlightText.length ? (
              <span className={styles.value} dangerouslySetInnerHTML={{ __html: applyHighlights(from) }} />
            ) : (
              <span className={styles.value}>{from}</span>
            )}
          </div>
          {to && (
            <div className={styles.metadataRow}>
              <span className={styles.label}>À:</span>
              {highlightText.length ? (
                <span className={styles.value} dangerouslySetInnerHTML={{ __html: applyHighlights(to) }} />
              ) : (
                <span className={styles.value}>{to}</span>
              )}
            </div>
          )}
          {Object.entries(metadata).map(([key, value]) => (
            <div key={key} className={styles.metadataRow}>
              <span className={styles.label}>{key.charAt(0).toUpperCase() + key.slice(1)}:</span>
              {highlightText.length ? (
                <span className={styles.value} dangerouslySetInnerHTML={{ __html: applyHighlights(String(value)) }} />
              ) : (
                <span className={styles.value}>{value}</span>
              )}
            </div>
          ))}
        </div>

        {highlightText.length ? (
          <div className={styles.subject} dangerouslySetInnerHTML={{ __html: applyHighlights(subject) }} />
        ) : (
          <div className={styles.subject}>{subject}</div>
        )}

        <div className={styles.content} ref={contentRef}>
          <div dangerouslySetInnerHTML={{ __html: highlightText.length ? applyHighlightsInHtml(content).replace(/\n/g, '<br />') : content.replace(/\n/g, '<br />') }} />
        </div>

        {!readOnly && (
        <div className={styles.actions}>
          <button
            className={`${styles.actionButton} ${styles.replyButton}`}
            onClick={(e) => { e.stopPropagation(); onReply(); }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M7.793 2.232a.75.75 0 01-.025 1.06L3.622 7.25h10.003a5.375 5.375 0 010 10.75H10.75a.75.75 0 010-1.5h2.875a3.875 3.875 0 000-7.75H3.622l4.146 3.957a.75.75 0 01-1.036 1.085l-5.5-5.25a.75.75 0 010-1.085l5.5-5.25a.75.75 0 011.061.025z" clipRule="evenodd" />
            </svg>
            Répondre
          </button>
          <button
            className={`${styles.actionButton} ${styles.archiveButton}`}
            onClick={(e) => { e.stopPropagation(); onArchive(); }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M2 3a1 1 0 00-1 1v1a1 1 0 001 1h16a1 1 0 001-1V4a1 1 0 00-1-1H2z" />
              <path fillRule="evenodd" d="M2 7.5h16l-.811 7.71a2 2 0 01-1.99 1.79H4.802a2 2 0 01-1.99-1.79L2 7.5zm5.22 1.72a.75.75 0 011.06 0L10 10.94l1.72-1.72a.75.75 0 111.06 1.06L11.06 12l1.72 1.72a.75.75 0 11-1.06 1.06L10 13.06l-1.72 1.72a.75.75 0 01-1.06-1.06L8.94 12l-1.72-1.72a.75.75 0 010-1.06z" clipRule="evenodd" />
            </svg>
            Archiver
          </button>
          <button
            className={`${styles.actionButton} ${styles.reportButton}`}
            onClick={(e) => { e.stopPropagation(); onReport(); }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
            Signaler
          </button>
        </div>
      )}
      </div>
    </div>
  );
});

Mailbox.displayName = 'Mailbox';
